import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Star,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { 
  usePaymentMethods, 
  useCreateStripeCustomer
} from '@/hooks/useBilling';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function AddPaymentMethodForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { mutate: addPaymentMethod, isPending } = usePaymentMethods().addPaymentMethod;
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card element not found');

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
      });

      if (error) throw error;
      if (!paymentMethod) throw new Error('Failed to create payment method');

      addPaymentMethod(paymentMethod.id, {
        onSuccess: () => {
          onSuccess();
          toast.success('Payment method added successfully');
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add payment method');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>
      <Button 
        type="submit" 
        disabled={!stripe || isProcessing || isPending}
        className="w-full"
      >
        {isProcessing || isPending ? 'Processing...' : 'Add Payment Method'}
      </Button>
    </form>
  );
}

export function PaymentMethodsTab() {
  const { 
    data, 
    isLoading,
    removePaymentMethod,
    setDefaultPaymentMethod 
  } = usePaymentMethods();
  const { mutate: createCustomer, isPending: isCreatingCustomer } = useCreateStripeCustomer();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteMethodId, setDeleteMethodId] = useState<string | null>(null);

  // Debug environment variables and configuration
  useEffect(() => {
    const debugConfig = {
      supabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
      supabaseAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
      stripePublishableKey: !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
      hasStripeCustomer: data?.hasStripeCustomer,
      paymentMethodsCount: data?.savedMethods?.length || 0,
    };
    console.log('Payment Methods Tab Debug Config:', debugConfig);
    
    // Log any missing environment variables
    if (!import.meta.env.VITE_SUPABASE_URL) console.error('Missing VITE_SUPABASE_URL');
    if (!import.meta.env.VITE_SUPABASE_ANON_KEY) console.error('Missing VITE_SUPABASE_ANON_KEY');
    if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) console.error('Missing VITE_STRIPE_PUBLISHABLE_KEY');
  }, [data]);

  const handleAddPaymentMethod = async () => {
    try {
      // Check session first
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('No active session. Please log in again.');
        return;
      }
      
      console.log('Add payment method initiated. Session:', !!session, 'hasStripeCustomer:', data?.hasStripeCustomer);
      
      // If customer already exists, show dialog immediately
      if (data?.hasStripeCustomer) {
        console.log('Customer exists, showing dialog');
        setShowAddDialog(true);
        return;
      }
      
      // Show immediate feedback and create customer
      const toastId = toast.loading('Setting up your payment account...', { 
        id: 'create-customer',
        duration: 30000 // 30 second timeout
      });
      
      console.log('Creating Stripe customer...');
      
      // Ensure Stripe customer exists with timeout
      const timeout = setTimeout(() => {
        toast.dismiss(toastId);
        toast.error('Request timed out. Please try again.');
        console.error('Customer creation timed out after 30 seconds');
      }, 30000);
      
      createCustomer(undefined, {
        onSuccess: (data) => {
          clearTimeout(timeout);
          console.log('Customer creation successful:', data);
          toast.dismiss(toastId);
          toast.success('Payment account set up successfully!');
          
          // Force refresh payment methods data before showing dialog
          setTimeout(() => {
            console.log('Refetching payment methods and opening dialog');
            setShowAddDialog(true);
          }, 500); // Small delay to ensure data is refreshed
        },
        onError: (error) => {
          clearTimeout(timeout);
          console.error('Create customer error:', {
            error,
            message: error.message,
            stack: error.stack,
            hasStripeCustomer: data?.hasStripeCustomer
          });
          toast.dismiss(toastId);
          
          // Provide more specific error messages
          let errorMessage = error.message;
          if (errorMessage.includes('Missing Supabase configuration')) {
            errorMessage = 'Configuration error. Please contact support.';
          } else if (errorMessage.includes('Unauthorized')) {
            errorMessage = 'Session expired. Please refresh the page and try again.';
          } else if (errorMessage.includes('timeout')) {
            errorMessage = 'Request timed out. Please check your connection and try again.';
          } else if (errorMessage.includes('network')) {
            errorMessage = 'Network error. Please check your connection and try again.';
          }
          
          toast.error(`Failed to set up payment account: ${errorMessage}`);
        },
      });
    } catch (error) {
      console.error('Unexpected error in handleAddPaymentMethod:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>Loading...</CardDescription>
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10" />
                <div>
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  const paymentMethods = data?.savedMethods || [];
  const hasStripeCustomer = data?.hasStripeCustomer ?? true; // Default to true for backward compatibility

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>
                {!hasStripeCustomer ? 'Set up your first payment method to get started' : 'Manage your credit cards for purchases'}
              </CardDescription>
            </div>
            <Button onClick={handleAddPaymentMethod} size="sm" disabled={isCreatingCustomer}>
              <Plus className="h-4 w-4 mr-2" />
              {isCreatingCustomer ? 'Setting up...' : 'Add Card'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {!hasStripeCustomer 
                  ? 'No payment methods on file. Add your first card to get started with purchases and auto-refill.'
                  : 'No payment methods on file. Add a card to purchase credits and enable auto-refill.'
                }
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div 
                  key={method.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {method.card_brand ? method.card_brand.charAt(0).toUpperCase() + method.card_brand.slice(1) : 'Card'} •••• {method.card_last4}
                        </p>
                        {method.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expires {method.card_exp_month}/{method.card_exp_year}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!method.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDefaultPaymentMethod.mutate(method.stripe_payment_method_id)}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteMethodId(method.stripe_payment_method_id)}
                      disabled={paymentMethods.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Payment Method Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Add a credit or debit card to purchase credits
            </DialogDescription>
          </DialogHeader>
          <Elements stripe={stripePromise}>
            <AddPaymentMethodForm onSuccess={() => setShowAddDialog(false)} />
          </Elements>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteMethodId} onOpenChange={() => setDeleteMethodId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this payment method? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteMethodId) {
                  removePaymentMethod.mutate(deleteMethodId);
                  setDeleteMethodId(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 